import { t } from "i18next";
import { FilterIcon } from "@/shared/icons";
import { PersonRecord } from "@/common/PersonRecord";
import { EllipsisWrapper } from "@/shared/Components";
import { transformSDateToString } from "@/shared/utils";
import type { ConnectionsTableData, ConnectionsTableProps } from "./types";
import type { Person } from "@/services/api";
import { connectionsTableColumnsKeys } from "./constants";

export const getConnectionsTableColumns: ({
  getAvatar,
}: {
  getAvatar: (person: Person) => string;
}) => ConnectionsTableProps["columns"] = ({ getAvatar }) => [
  {
    key: connectionsTableColumnsKeys.person,
    title: t("common.person"),
    dataIndex: ["person"],
    sorter: (a: ConnectionsTableData, b: ConnectionsTableData) =>
      (a?.connectionPerson?.firstName as string).localeCompare(
        b?.connectionPerson?.firstName as string,
      ),
    filterIcon: <FilterIcon />,
    onFilter: (value, record) => record.connectionPerson?.firstName === value,
    render: (_, record: ConnectionsTableData) => (
      <PersonRecord
        avatar={getAvatar(record.connectionPerson)}
        hideTitle
        group={record.connectionPerson.group ?? ""}
        name={`${record.connectionPerson.firstName ?? ""} ${record.connectionPerson.lastName ?? ""}`}
        selected={false}
        title={""}
      />
    ),
  },
  {
    key: connectionsTableColumnsKeys.activity,
    title: t("contactsPage.activities.activity"),
    dataIndex: ["activity"],
    filterIcon: <FilterIcon />,
    ellipsis: { showTitle: false },
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>
        {record?.connectionPerson?.professionalActivities?.[0]?.activity ?? ""}
      </EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.additionalInformation,
    ellipsis: { showTitle: false },
    title: t("contactsPage.additionalInfo"),
    dataIndex: ["additionalInformation"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>{record?.additionalInfo ?? ""}</EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.birthday,
    title: t("settingsPage.general.birthday"),
    ellipsis: { showTitle: false },
    dataIndex: ["birthday"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>
        {transformSDateToString(record?.connectionPerson?.birthdaySDate) ?? ""}
      </EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.connectedAs,
    ellipsis: { showTitle: false },
    title: t("contactsPage.connectedAs"),
    dataIndex: ["connectedAs"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>{record?.personConnectionType?.name ?? ""}</EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.industry,
    title: t("contactsPage.activities.industry"),
    ellipsis: { showTitle: false },
    dataIndex: ["industry"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>
        {record?.connectionPerson?.careers?.[0]?.industry?.name ?? ""}
      </EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.role,
    title: t("contactsPage.activities.role"),
    ellipsis: { showTitle: false },
    dataIndex: ["role"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>
        {record?.connectionPerson?.professionalActivities?.[0]?.role ?? ""}
      </EllipsisWrapper>
    ),
  },
  {
    key: connectionsTableColumnsKeys.title,
    title: t("common.title"),
    ellipsis: { showTitle: false },
    dataIndex: ["title"],
    filterIcon: <FilterIcon />,
    render: (_, record: ConnectionsTableData) => (
      <EllipsisWrapper>{record?.connectionPerson?.title ?? ""}</EllipsisWrapper>
    ),
  },
];
